import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  RoutingMetricsResponseSchema,
  RoutingPoliciesResponseSchema,
  RoutingPolicyDetailResponseSchema,
  ActivatePolicyResponseSchema,
  DryRunResponseSchema,
} from "@/api/schemas";
import type { TimeRange } from "@/components/TimeRangeSelector";

export function useRoutingMetrics(tenantId: string, range: TimeRange, userId = "") {
  return useQuery({
    queryKey: ["routing-metrics", tenantId, range.from, range.to, userId],
    queryFn: async () => {
      const searchParams: Record<string, string> = {
        from: range.from,
        to: range.to,
      };
      if (userId) searchParams.user_id = userId;
      const json = await api.get("routing/metrics", { searchParams }).json();
      return RoutingMetricsResponseSchema.parse(json);
    },
    enabled: !!tenantId,
  });
}

export function useRoutingPolicies(tenantId: string) {
  return useQuery({
    queryKey: ["routing-policies", tenantId],
    queryFn: async () => {
      const json = await api
        .get("routing/policies")
        .json();
      return RoutingPoliciesResponseSchema.parse(json);
    },
    enabled: !!tenantId,
  });
}

export function useRoutingPolicyDetail(policyId: string) {
  return useQuery({
    queryKey: ["routing-policy-detail", policyId],
    queryFn: async () => {
      const json = await api.get(`routing/policies/${policyId}`).json();
      return RoutingPolicyDetailResponseSchema.parse(json);
    },
    enabled: !!policyId,
  });
}

export function useActivatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      policyId,
      tenantId: _tenantId,
    }: {
      policyId: string;
      tenantId: string;
    }) => {
      const json = await api
        .post(`routing/policies/${policyId}/activate`)
        .json();
      return ActivatePolicyResponseSchema.parse(json);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["routing-policies", variables.tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["routing-policy-detail", variables.policyId],
      });
    },
  });
}

export function useDryRun() {
  return useMutation({
    mutationFn: async (body: {
      payload: Record<string, unknown>;
      feature?: string;
      env?: string;
      priority?: string;
      user_id?: string;
    }) => {
      const json = await api
        .post("routing/dry-run", { json: body })
        .json();
      return DryRunResponseSchema.parse(json);
    },
  });
}

